import React, { useEffect, useRef, useState } from "react";
import { useLookupSwachhataSchoolMutation } from "../../../redux/auth/authApi";

const CLASSES = ["6", "7", "8", "9", "10", "11", "12"];
const NAME_RX = /^[\p{L}][\p{L}\p{M} .'’-]{1,79}$/u;
// UDISE codes are exactly 11 characters; letters and digits only.
const UDISE_RX = /^[A-Z0-9]{11}$/;

/*
 * Public student registration — collected before the quiz so each attempt is
 * tied to a school (UDISE). No account is created.
 *
 * The school is looked up on the UDISE+ API (through our server, which caches
 * it) as soon as the code is complete. The server returns a signed
 * `schoolToken` with the result, passed on to start so the school is not
 * looked up twice. If UDISE+ is not responding the student can still start;
 * the server matches the school again on submit.
 */
export default function SwachhataStudentForm({ onStart, starting, serverError, initial }) {
  const [name, setName] = useState(initial?.name || "");
  const [klass, setKlass] = useState(initial?.class || "");
  const [age, setAge] = useState(initial?.age || "");
  const [udise, setUdise] = useState(initial?.udise_id || "");
  const [touched, setTouched] = useState({});
  const [school, setSchool] = useState(null); // { udise, found, school, token, unavailable }
  const [lookup, { isLoading: looking }] = useLookupSwachhataSchoolMutation();
  const lastLooked = useRef("");

  const cleanUdise = udise.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  const errors = {
    name: !NAME_RX.test(name.replace(/\s+/g, " ").trim()) ? "Enter your full name (letters only)." : "",
    class: !CLASSES.includes(klass) ? "Select your class." : "",
    age: !(Number(age) >= 10 && Number(age) <= 20 && Number.isInteger(Number(age))) ? "Enter your age (10–20)." : "",
    udise_id: !UDISE_RX.test(cleanUdise) ? "Enter your school's 11-character UDISE code (letters and numbers only)." : "",
  };
  const valid = !Object.values(errors).some(Boolean);

  // Look the school up as soon as the UDISE looks complete.
  useEffect(() => {
    if (!UDISE_RX.test(cleanUdise) || cleanUdise === lastLooked.current) return undefined;
    const t = setTimeout(async () => {
      lastLooked.current = cleanUdise;
      try {
        const res = await lookup({ udise_id: cleanUdise }).unwrap();
        setSchool({ udise: cleanUdise, found: !!res.found, school: res.school || null, token: res.schoolToken || "" });
      } catch (e) {
        // A code the server refuses for the student quiz (the internal test
        // UDISE): keep it rejected, do not offer the "start anyway" path.
        if (e?.data?.code === "UDISE_NOT_ALLOWED") {
          setSchool({ udise: cleanUdise, found: false, school: null, token: "", blocked: true, message: e?.data?.message || "" });
          return;
        }
        // 503 UDISE_UNAVAILABLE (or offline): allow a retry of the same code.
        lastLooked.current = "";
        setSchool({ udise: cleanUdise, found: false, school: null, token: "", unavailable: true });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [cleanUdise, lookup]);

  const show = (k) => touched[k] && errors[k];
  const current = school && school.udise === cleanUdise ? school : null;
  const submit = (e) => {
    e.preventDefault();
    setTouched({ name: true, class: true, age: true, udise_id: true });
    if (!valid || starting) return;
    // A code UDISE+ (and our own school list) does not know is a typo — the
    // attempt would be saved with no State / UT. An upstream outage
    // (`unavailable`) still goes through.
    if (current && !current.found && (!current.unavailable || current.blocked)) return;
    onStart({
      name: name.replace(/\s+/g, " ").trim(),
      class: klass,
      age: Number(age),
      udise_id: cleanUdise,
      schoolToken: current?.token || undefined,
    });
  };

  return (
    <form className="shs-form" onSubmit={submit} noValidate>
      <div className="shs-form__grid">
        <label className={`shs-field ${show("name") ? "is-error" : ""}`}>
          <span className="shs-field__label">Student name</span>
          <input
            type="text" value={name} maxLength={80} autoComplete="name" placeholder="e.g. Aarav Sharma"
            onChange={(e) => setName(e.target.value)} onBlur={() => setTouched((t) => ({ ...t, name: true }))}
          />
          {show("name") && <small className="shs-field__err">{errors.name}</small>}
        </label>
        <label className={`shs-field ${show("class") ? "is-error" : ""}`}>
          <span className="shs-field__label">Class</span>
          <select value={klass} onChange={(e) => setKlass(e.target.value)} onBlur={() => setTouched((t) => ({ ...t, class: true }))}>
            <option value="">Select class</option>
            {CLASSES.map((c) => <option key={c} value={c}>{`Class ${c}`}</option>)}
          </select>
          {show("class") && <small className="shs-field__err">{errors.class}</small>}
        </label>
        <label className={`shs-field ${show("age") ? "is-error" : ""}`}>
          <span className="shs-field__label">Age</span>
          <input
            type="number" inputMode="numeric" min={10} max={20} value={age} placeholder="e.g. 12"
            onChange={(e) => setAge(e.target.value)} onBlur={() => setTouched((t) => ({ ...t, age: true }))}
          />
          {show("age") && <small className="shs-field__err">{errors.age}</small>}
        </label>
        <label className={`shs-field shs-field--wide ${show("udise_id") ? "is-error" : ""}`}>
          <span className="shs-field__label">School UDISE code</span>
          <input
            type="text" value={udise} maxLength={11} placeholder="11-character UDISE code of your school" autoCapitalize="characters" spellCheck={false}
            onChange={(e) => setUdise(e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 11))} onBlur={() => setTouched((t) => ({ ...t, udise_id: true }))}
          />
          {show("udise_id") && <small className="shs-field__err">{errors.udise_id}</small>}
          <small className="shs-field__hint">Ask your teacher if you are not sure — it is printed on school documents and the Eco Club portal.</small>
        </label>
      </div>

      {UDISE_RX.test(cleanUdise) && (
        <div className={`shs-school ${current?.found ? "is-found" : current ? "is-missing" : ""}`}>
          {looking || !current ? (
            <><span className="shs-spinner shs-spinner--sm" /><span>Finding your school on UDISE+…</span></>
          ) : current.found ? (
            <>
              <span className="shs-school__icon">🏫</span>
              <div>
                <b>{current.school.school_name}</b>
                <small>{`${[current.school.district, current.school.state].filter(Boolean).join(", ")} · UDISE ${cleanUdise}`}</small>
              </div>
            </>
          ) : current.blocked ? (
            <>
              <span className="shs-school__icon">🚫</span>
              <div>
                <b>This UDISE code cannot be used for the quiz</b>
                <small>{current.message || "It is an internal test code. Please enter your own school's 11-character UDISE code."}</small>
              </div>
            </>
          ) : current.unavailable ? (
            <>
              <span className="shs-school__icon">📡</span>
              <div>
                <b>UDISE+ is not responding right now</b>
                <small>You can still start the quiz — your school will be matched when you submit.</small>
              </div>
            </>
          ) : (
            <>
              <span className="shs-school__icon">🔎</span>
              <div>
                <b>{`No school found for ${cleanUdise}`}</b>
                <small>Please check the code with your teacher and enter it again — the quiz starts once your school is found.</small>
              </div>
            </>
          )}
        </div>
      )}

      {serverError && <div className="shs-error">{serverError}</div>}

      <div className="shs-form__actions shs-form__actions--end">
        <small className="shs-form__note">10 random questions · score 3 or more for a certificate · your details are used only for the campaign report.</small>
        <button
          type="submit"
          className="shs-btn shs-btn--orange shs-btn--lg"
          disabled={starting || Boolean(current && !current.found && (!current.unavailable || current.blocked))}
        >
          {starting ? "Preparing your paper…" : "Start the quiz"} <span className="shs-btn__arrow">→</span>
        </button>
      </div>
    </form>
  );
}
